
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Calendar, Filter, Target, X, Users, Trophy, Layers } from 'lucide-react';
import { DivisionManagement } from '@/components/admin/DivisionManagement';

interface ClubSeasonManagementProps {
  clubId: string;
}

interface SeasonStats {
  id: string;
  season_year: number;
  name: string;
  club_id: string;
  status: 'active' | 'upcoming' | 'inactive';
  total_teams: number;
  total_divisions: number;
  total_players: number;
}

type StatusFilter = 'active' | 'upcoming' | 'inactive';

export const ClubSeasonManagement = ({ clubId }: ClubSeasonManagementProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState<SeasonStats | null>(null);
  const [seasonYear, setSeasonYear] = useState('');
  const [seasonName, setSeasonName] = useState('');
  const [seasonStatus, setSeasonStatus] = useState<'active' | 'upcoming' | 'inactive'>('upcoming');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch season statistics for this club
  const { data: seasons, isLoading: seasonsLoading } = useQuery({
    queryKey: ['club-season-stats', clubId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('season_stats')
        .select('*')
        .eq('club_id', clubId)
        .order('season_year', { ascending: false });
      
      if (error) {
        console.error('Error fetching season stats:', error);
        throw error;
      }
      return data as SeasonStats[];
    },
  });

  // Filter seasons based on status
  const filteredSeasons = seasons?.filter(season => 
    season.status === statusFilter
  ) || [];

  // Get selected season for division management
  const selectedSeason = seasons?.find(s => s.id === selectedSeasonId);

  // Add season mutation
  const addSeasonMutation = useMutation({
    mutationFn: async ({ year, name, status }: { year: number; name: string; status: 'active' | 'upcoming' | 'inactive' }) => {
      const { error } = await supabase
        .from('seasons')
        .insert([{
          season_year: year,
          name: name,
          status: status,
          club_id: clubId,
        }]);
      
      if (error) {
        console.error('Error adding season:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club-season-stats', clubId] });
      toast.success('Season added successfully');
      handleAddDialogClose();
    },
    onError: (error: Error) => {
      console.error('Add season error:', error);
      toast.error('Failed to add season');
    },
  });

  // Update season mutation
  const updateSeasonMutation = useMutation({
    mutationFn: async ({ seasonId, year, name, status }: { seasonId: string; year: number; name: string; status: 'active' | 'upcoming' | 'inactive' }) => {
      const { error } = await supabase
        .from('seasons')
        .update({
          season_year: year,
          name: name,
          status: status,
        })
        .eq('id', seasonId);
      
      if (error) {
        console.error('Error updating season:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club-season-stats', clubId] });
      toast.success('Season updated successfully');
      setIsEditDialogOpen(false);
      // Reset form state
      setEditingSeason(null);
      setSeasonYear('');
      setSeasonName('');
      setSeasonStatus('upcoming');
    },
    onError: (error: Error) => {
      console.error('Update season error:', error);
      toast.error('Failed to update season');
    },
  });

  // Delete season mutation
  const deleteSeasonMutation = useMutation({
    mutationFn: async (seasonId: string) => {
      const { error } = await supabase
        .from('seasons')
        .delete()
        .eq('id', seasonId);
      
      if (error) {
        console.error('Error deleting season:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club-season-stats', clubId] });
      toast.success('Season deleted successfully');
      // Clear selection if deleted season was selected
      if (selectedSeasonId && seasons?.find(s => s.id === selectedSeasonId)) {
        setSelectedSeasonId(null);
      }
    },
    onError: (error) => {
      console.error('Delete season error:', error);
      toast.error('Failed to delete season');
    },
  });

  const handleAddSeason = () => {
    if (!seasonYear || !seasonName) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const year = parseInt(seasonYear);
    if (isNaN(year)) {
      toast.error('Please enter a valid year');
      return;
    }
    
    addSeasonMutation.mutate({ year, name: seasonName, status: seasonStatus });
  };

  const handleEditSeason = (season: SeasonStats) => {
    setEditingSeason(season);
    setSeasonYear(season.season_year.toString());
    setSeasonName(season.name);
    setSeasonStatus(season.status);
    setIsEditDialogOpen(true);
  };

  const handleUpdateSeason = () => {
    if (!editingSeason || !seasonYear || !seasonName) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const year = parseInt(seasonYear);
    if (isNaN(year)) {
      toast.error('Please enter a valid year');
      return;
    }
    
    updateSeasonMutation.mutate({ 
      seasonId: editingSeason.id, 
      year, 
      name: seasonName,
      status: seasonStatus
    });
  };

  const handleDeleteSeason = (seasonId: string) => {
    if (confirm('Are you sure you want to delete this season? This will also delete all associated teams.')) {
      deleteSeasonMutation.mutate(seasonId);
    }
  };

  const handleAddDialogClose = () => {
    setIsAddDialogOpen(false);
    setSeasonYear('');
    setSeasonName('');
    setSeasonStatus('upcoming');
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    setEditingSeason(null);
    setSeasonYear('');
    setSeasonName('');
    setSeasonStatus('upcoming');
  };

  const handleSelectSeason = (seasonId: string) => {
    setSelectedSeasonId(seasonId);
  };

  const handleClearSelection = () => {
    setSelectedSeasonId(null);
  };

  const getStatusBadgeVariant = (status: 'active' | 'upcoming' | 'inactive') => {
    switch (status) {
      case 'active':
        return 'secondary';
      case 'upcoming':
        return 'secondary';
      case 'inactive':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusColor = (status: 'active' | 'upcoming' | 'inactive') => {
    switch (status) {
      case 'active':
        return 'text-green-600';
      case 'upcoming':
        return 'text-blue-600';
      case 'inactive':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusBulletColor = (status: 'active' | 'upcoming' | 'inactive') => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'upcoming':
        return 'bg-blue-500';
      case 'inactive':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  if (seasonsLoading) {
    return <div>Loading seasons...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with Add Button and Filter */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">Seasons ({filteredSeasons.length})</h3>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={(value: StatusFilter) => setStatusFilter(value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Season
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Season</DialogTitle>
              <DialogDescription>
                Create a new season for this club.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="season-year">Season Year</Label>
                <Input
                  id="season-year"
                  type="number"
                  placeholder="e.g., 2027"
                  value={seasonYear}
                  onChange={(e) => setSeasonYear(e.target.value)}
                  autoComplete="off"
                />
              </div>
              
              <div>
                <Label htmlFor="season-name">Season Name</Label>
                <Input
                  id="season-name"
                  placeholder="e.g., 2027 Fall Season"
                  value={seasonName}
                  onChange={(e) => setSeasonName(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div>
                <Label htmlFor="season-status">Status</Label>
                <Select value={seasonStatus} onValueChange={(value: 'active' | 'upcoming' | 'inactive') => setSeasonStatus(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleAddDialogClose}>
                  Cancel
                </Button>
                <Button 
                  type="button"
                  onClick={handleAddSeason}
                  disabled={addSeasonMutation.isPending}
                >
                  {addSeasonMutation.isPending ? 'Adding...' : 'Add Season'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Season Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          handleEditDialogClose();
        } else {
          setIsEditDialogOpen(open);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Season</DialogTitle>
            <DialogDescription>
              Update the season details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-season-year">Season Year</Label>
              <Input
                id="edit-season-year"
                type="number"
                placeholder="e.g., 2025"
                value={seasonYear}
                onChange={(e) => setSeasonYear(e.target.value)}
                autoComplete="off"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-season-name">Season Name</Label>
              <Input
                id="edit-season-name"
                placeholder="e.g., 2025 Fall Season"
                value={seasonName}
                onChange={(e) => setSeasonName(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div>
              <Label htmlFor="edit-season-status">Status</Label>
              <Select value={seasonStatus} onValueChange={(value: 'active' | 'upcoming' | 'inactive') => setSeasonStatus(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleEditDialogClose}>
                Cancel
              </Button>
              <Button 
                type="button"
                onClick={handleUpdateSeason}
                disabled={updateSeasonMutation.isPending}
              >
                {updateSeasonMutation.isPending ? 'Updating...' : 'Update Season'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Seasons Table */}
      {filteredSeasons && filteredSeasons.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Year</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Trophy className="h-4 w-4" />
                  Teams
                </div>
              </TableHead>
              <TableHead className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Layers className="h-4 w-4" />
                  Divisions
                </div>
              </TableHead>
              <TableHead className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Users className="h-4 w-4" />
                  Players
                </div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSeasons.map((season) => (
              <TableRow 
                key={season.id}
                className={`cursor-pointer hover:bg-muted/50 ${
                  selectedSeasonId === season.id ? 'bg-muted' : ''
                }`}
                onClick={() => handleSelectSeason(season.id)}
              >
                <TableCell className={`font-medium ${
                  selectedSeasonId === season.id ? 'border-l-4 border-l-primary rounded-l-md' : ''
                }`}>
                  {season.season_year}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${getStatusBulletColor(season.status)}`}></div>
                    {season.name}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge 
                    variant={getStatusBadgeVariant(season.status)}
                    className={getStatusColor(season.status)}
                  >
                    {season.status.charAt(0).toUpperCase() + season.status.slice(1)}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <span className="font-medium text-lg">{season.total_teams}</span>
                </TableCell>
                <TableCell className="text-center">
                  <span className="font-medium text-lg">{season.total_divisions}</span>
                </TableCell>
                <TableCell className="text-center">
                  <span className="font-medium text-lg">{season.total_players}</span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditSeason(season)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSeason(season.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSelectSeason(season.id)}
                      className={selectedSeasonId === season.id ? 'bg-primary/10' : ''}
                    >
                      <Target className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="py-8 text-center">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            No {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Seasons
          </h3>
          <p className="text-muted-foreground mb-4">
            This club doesn't have any {statusFilter} seasons.
          </p>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Season
          </Button>
        </div>
      )}

      {/* Division Management Detail View */}
      {selectedSeason && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${getStatusBulletColor(selectedSeason.status)}`}></div>
              <Target className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Divisions for {selectedSeason.name}</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSelection}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div>
            <DivisionManagement 
              seasonId={selectedSeason.id} 
              seasonName={`${selectedSeason.season_year} - ${selectedSeason.name}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
